import { ErrorBoundary } from '@sentry/react';
import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAdminStore } from '@/store/admin';
import { ErrorContent } from '@/components/ErrorContent';
import { RequireAuth } from '@/routes/RequireAuth';
import { DataSourcesPage } from '@/pages/Datasets/DataSources/index';
import { WebConnectorsList } from '@/components/Datasets/WebConnectors/index';
import { KnowledgeLoop } from '@/components/Datasets/KnowledgeLoop/index';
import { DataSourcesList } from '@/components/Datasets/DataSources/list';
import { FileChunksList } from '@/components/Datasets/Chunk/list';
import { SiteList } from '@/components/Datasets/DataSources/SiteList';
import { NewAdminConsole } from '@/pages/NewAdminConsole';

import LazyChatPage from '@/pages/Bot/Chat';
import LazyDashboardPage from '@/pages/Bot/Dashboard';
import LazyVisitorsPage from '@/pages/Bot/Visitors';
import LazyDataSourcePage from '@/pages/Bot/DataSource';
import LazySettingsPage from '@/pages/Bot/Settings';
import LazyToolsPage from '@/pages/Bot/Tools';
import LazyCapabilitiesPage from '@/pages/Bot/Capabilities';
import LazyWidgetEmbedPage from '@/pages/Bot/WidgetEmbed';
import LazyDigitalHumanPage from '@/pages/Bot/DigitalHuman';
import LazyQuestionsPage from '@/pages/Bot/Questions';
import LazyInsightsPage from '@/pages/Bot/Insights';
import LazyUsagePage from '@/pages/Bot/Usage';
import LazyDictionaryPage from '@/pages/Bot/Dictionary';
import LazyRecordsPage from '@/pages/Bot/Records';
import LazyTagPage from '@/pages/Bot/Tag';
import LazyRealtimePage from '@/pages/Bot/Realtime';
import LazyPhonePage from '@/pages/Bot/Phone';
import LazyZendeskOpsPage from '@/pages/Bot/Zendesk';
// Bot Detail

const HomepagePage = lazy(() => import('@/pages/Bot/Homepage'));
const HomepageTemplatesPage = lazy(() => import('@/pages/HomepageTemplates'));

export const BotRoutes = () => {
  return (
    <Routes>
      <Route path="/chat" element={<LazyChatPage />} />
      <Route path="/dashboard" element={<LazyDashboardPage />} />
      <Route path="/visitors" element={<LazyVisitorsPage />} />
      <Route path="/data-source" element={<LazyDataSourcePage />} />
      <Route path="/settings" element={<LazySettingsPage />} />
      <Route path="/realtime" element={<LazyRealtimePage />} />
      <Route path="/phone" element={<LazyPhonePage />} />
      <Route path="/zendesk" element={<LazyZendeskOpsPage />} />
      <Route path="/tools" element={<LazyToolsPage />} />
      <Route path="/capabilities" element={<LazyCapabilitiesPage />} />
      <Route path="/digital-human" element={<LazyDigitalHumanPage />} />
      <Route path="/widget-embed" element={<LazyWidgetEmbedPage />} />
      <Route path="/homepage" element={<HomepagePage />} />
      <Route path="/questions" element={<LazyQuestionsPage />} />
      <Route path="/insights" element={<LazyInsightsPage />} />
      <Route path="/usage" element={<LazyUsagePage />} />
      <Route path="/dictionary" element={<LazyDictionaryPage />} />
      <Route path="/records" element={<LazyRecordsPage />} />
      <Route path="/datasets/:datasetId/*" element={<LazyDatasets />} />
      <Route path="/tag" element={<LazyTagPage />} />

      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
};

export const AssistRoutes = () => {
  return (
    <Routes>
      <Route path="/chat" element={<LazyChatPage />} />
      <Route path="/dashboard" element={<LazyDashboardPage />} />
      <Route path="/visitors" element={<LazyVisitorsPage />} />
      <Route path="/data-source" element={<LazyDataSourcePage />} />
      <Route path="/settings" element={<LazySettingsPage />} />
      <Route path="/realtime" element={<LazyRealtimePage />} />
      <Route path="/phone" element={<LazyPhonePage />} />
      <Route path="/zendesk" element={<LazyZendeskOpsPage />} />
      <Route path="/tools" element={<LazyToolsPage />} />
      <Route path="/capabilities" element={<LazyCapabilitiesPage />} />
      <Route path="/digital-human" element={<LazyDigitalHumanPage />} />
      <Route path="/widget-embed" element={<LazyWidgetEmbedPage />} />
      <Route path="/questions" element={<LazyQuestionsPage />} />
      <Route path="/insights" element={<LazyInsightsPage />} />
      <Route path="/usage" element={<LazyUsagePage />} />
      <Route path="/dictionary" element={<LazyDictionaryPage />} />
      <Route path="/records" element={<LazyRecordsPage />} />
      <Route path="/datasets/:datasetId/*" element={<LazyDatasets />} />
      <Route path="/tag" element={<LazyTagPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
};

// Datasets
import LazyDataBaseImportPage from '@/pages/Datasets/DataBase/import';
import LazyDataBasePropertyPage from '@/pages/Datasets/DataBase/property';
export const DatasetsRoutes = () => {
  return (
    <Routes>
      <Route path="/data-source/*" element={<DataSourcesPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
};

// Datasets Sources
import LazyDataBasePage from '@/pages/Datasets/DataBase';
import LazyAPIsPage from '@/pages/Datasets/APIs';
import LazyIntegrationTasksPage from '@/pages/Datasets/Integration/Tasks';

export const DatasetSourcesRoutes = () => {
  return (
    <Routes>
      <Route path="/faq" element={<LazyDataBasePage />} />
      <Route path="/api" element={<LazyAPIsPage />} />
      <Route path="/integration" element={<LazyIntegrationTasksPage />} />
      <Route path="/integration/:ruleId" element={<DataSourcesList />} />
      <Route path="/integration/:ruleId/chunk/:fileId" element={<FileChunksList />} />
      <Route path="/data-base/import" element={<LazyDataBaseImportPage />} />
      <Route path="/data-base/property" element={<LazyDataBasePropertyPage />} />
      <Route path="/data-base" element={<LazyDataBasePage />} />
      <Route path="/file/:fileId" element={<FileChunksList />} />
      <Route path="/website/:fileId" element={<FileChunksList />} />
      <Route path="/web-connector" element={<WebConnectorsList />} />
      <Route path="/web-connector/:connectorId" element={<SiteList />} />
      <Route path="/web-connector/:connectorId/:fileId" element={<FileChunksList />} />
      <Route path="/zendesk" element={<KnowledgeLoop />} />
      <Route path="/*" element={<DataSourcesList />} />
    </Routes>
  );
};

// Main

import LazyBotsPage from '@/pages/Bots';
import LazyBotPage from '@/pages/Bot';
import LazyTeamPage from '@/pages/Team';
import LazyAccountPage from '@/pages/Account';
import LazyApiKeysPage from '@/pages/ApiKeys';
import LazyCreatePage from '@/pages/Create';
import LazyDatasets from '@/pages/Datasets';
import LazyCollaborativePage from '@/pages/Collaborative';
import LazyAccessPage from '@/pages/Access';
import LazyAssistPage from '@/pages/Assist';
import LazyAdminConsolePage from '@/pages/AdminConsole';
import LazyMessageCenterPage from '@/pages/MessageCenter';
import SecurityPolicyPage from '@/pages/SecurityPolicy';
// Dealer (代理店) v0.5
import DealersListPage from '@/pages/Dealers';
import DealerDetailPage from '@/pages/Dealers/Detail';
import DealerPortalPage from '@/pages/DealerPortal';

const ProtectedRoute = ({ element, hasAccess }: { element: JSX.Element; hasAccess: boolean }) => {
  return hasAccess ? element : <Navigate to="/no-access" replace />;
};

export const MainRoutes = () => {
  const { hasAdminConsoleAuthority } = useAdminStore();

  return (
    <Routes>
      <Route path="/create" element={<LazyCreatePage />} />
      <Route path="/bots" element={<LazyBotsPage />} />
      <Route path="/homepage-templates" element={<HomepageTemplatesPage />} />
      <Route path="/bot/:id/*" element={<LazyBotPage />} />
      <Route path="/datasets/:datasetId/*" element={<LazyDatasets />} />
      <Route path="/datasets" element={<LazyDatasets />} />
      <Route path="/team" element={<LazyTeamPage />} />
      <Route path="/account" element={<LazyAccountPage />} />
      <Route path="/keys" element={<LazyApiKeysPage />} />
      <Route path="/collaborative" element={<LazyCollaborativePage />} />
      <Route path="/access" element={<LazyAccessPage />} />
      <Route path="/assist/:id/*" element={<LazyAssistPage />} />
      <Route path="/admin-console" element={<LazyAdminConsolePage />} />
      <Route path="/new-admin-console" element={<NewAdminConsole />} />
      {/* 企业安全策略 —— 菜单可见性由 hasAdminConsoleAuthority 控制，数据访问由后端接口鉴权 */}
      <Route path="/security-policy" element={<SecurityPolicyPage />} />
      {/* Dealer (代理店) 路由 v0.5 —— 鉴权由组件内部检查 userconfigs 完成 */}
      <Route path="/dealers" element={<DealersListPage />} />
      {/* /dealers/overview 已合并入 /dealers，保留 alias 兼容旧链接 */}
      <Route path="/dealers/overview" element={<Navigate to="/dealers" replace />} />
      <Route path="/dealers/:id" element={<DealerDetailPage />} />
      <Route path="/dealers/me" element={<DealerPortalPage />} />
      {/* Cloud版消息中心 */}
      <Route path="/message-center" element={<LazyMessageCenterPage />} />
      {/* <Route
        path="/admin-console"
        element={<ProtectedRoute element={<LazyAdminConsolePage />} hasAccess={!!hasAdminConsoleAuthority} />}
      /> */}
      <Route path="/" element={<Navigate to="/bots" replace />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
};

import LazyLogin from '@/pages/Login';
import LazyRegister from '@/pages/Register';
import LazyVerifyEmailPage from '@/pages/VerifyPage';
import LazyForgotPassword from '@/pages/ForgotPassword';
import LazyLoginCallbackPage from '@/pages/LoginCallback';
import LazyResetPasswordPage from '@/pages/ResetPassword';
import LazyAutoSetupPage from '@/pages/AutoSetup';
import LazyNotFoundPage from '@/pages/NotFoundPage';
import LazyMainPage from '@/pages/Main';
import LazyDebugChunksPage from '@/pages/Debug/Chunks';
import LazyWidgetPreviewPage from '@/pages/Bot/WidgetPreview';
import LazyNoAccessPage from '@/pages/NoAccess';
import LazyWidgetPage from '@/pages/ChatWidget';
import LazyGbaseAuthSuccessPage from '@/pages/GbaseAuthSuccess';

export const AppRoutes = () => {
  return (
    <ErrorBoundary fallback={({ error }) => <ErrorContent error={error} />}>
      <Routes>
        <Route path="/login" element={<LazyLoginCallbackPage />} />
        <Route
          path="/auto-setup"
          element={
            <RequireAuth>
              <LazyAutoSetupPage />
            </RequireAuth>
          }
        />
        <Route path="/auth/login" element={<LazyLogin />} />
        <Route path="/auth/register" element={<LazyRegister />} />
        <Route path="/auth/verify-email" element={<LazyVerifyEmailPage />} />
        <Route path="/reset-password" element={<LazyResetPasswordPage />} />
        <Route path="/auth/forgot-password" element={<LazyForgotPassword />} />
        <Route path="/gbase-auth-success" element={<LazyGbaseAuthSuccessPage />} />
        <Route path="/404" element={<LazyNotFoundPage />} />
        <Route path="/debug/chunks" element={<LazyDebugChunksPage />} />
        <Route path="/widget-preview" element={<LazyWidgetPreviewPage />} />
        <Route path="/no-access" element={<LazyNoAccessPage />} />
        <Route path="/widget/:id" element={<LazyWidgetPage />} />
        <Route
          path="*"
          element={
            <RequireAuth>
              <LazyMainPage />
            </RequireAuth>
          }
        />
      </Routes>
    </ErrorBoundary>
  );
};
